import { useState, useCallback } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { showSuccessToast } from "@/components/NotificationToast";
import { TaxRequestList } from "./components/TaxRequestList";
import { TaxRequestUploadDialog } from "./components/TaxRequestUploadDialog";
import { TaxRequestReviewPage } from "./components/TaxRequestReviewPage";
import { mockTaxRequestJobs } from "./mockData";
import type { TaxRequestJob } from "./types";

export function TaxRequestsPage() {
  const [jobs, setJobs] = useState<TaxRequestJob[]>(mockTaxRequestJobs);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  const selectedJob = jobs.find((j) => j.id === selectedJobId) || null;

  const handleNewRequest = useCallback(
    (filename: string, clientId: string) => {
      const newJob: TaxRequestJob = {
        id: `tr-${Date.now()}`,
        clientId,
        requestFilename: filename,
        requestDate: new Date().toISOString().split("T")[0],
        status: "processing",
        totalRequested: 6,
        totalFound: 0,
        totalMissing: 0,
        totalAmbiguous: 0,
        items: [],
      };

      setJobs((prev) => [newJob, ...prev]);
      showSuccessToast("Tax request submitted for processing");

      // Simulate processing
      setTimeout(() => {
        setJobs((prev) =>
          prev.map((j) =>
            j.id === newJob.id
              ? {
                  ...j,
                  status: "completed_with_issues" as const,
                  totalFound: 4,
                  totalMissing: 1,
                  totalAmbiguous: 1,
                  items: [
                    {
                      id: `${j.id}-i1`,
                      requestedDescription: "Фактура от доставчик за Януари 2025",
                      requestedDocumentType: "invoice_inbound",
                      requestedDocumentNumber: null,
                      requestedDocumentDate: "2025-01-15",
                      requestedCounterparty: null,
                      status: "found" as const,
                      matchedDocumentId: "d1",
                      matchNotes: null,
                      candidateDocumentIds: [],
                    },
                    {
                      id: `${j.id}-i2`,
                      requestedDescription: "Издадена фактура №0089",
                      requestedDocumentType: "invoice_outbound",
                      requestedDocumentNumber: "0089",
                      requestedDocumentDate: null,
                      requestedCounterparty: null,
                      status: "found" as const,
                      matchedDocumentId: "d3",
                      matchNotes: null,
                      candidateDocumentIds: [],
                    },
                    {
                      id: `${j.id}-i3`,
                      requestedDescription: "Договор за наем",
                      requestedDocumentType: "contract",
                      requestedDocumentNumber: null,
                      requestedDocumentDate: null,
                      requestedCounterparty: null,
                      status: "found" as const,
                      matchedDocumentId: "d4",
                      matchNotes: null,
                      candidateDocumentIds: [],
                    },
                    {
                      id: `${j.id}-i4`,
                      requestedDescription: "Банково извлечение Януари 2025",
                      requestedDocumentType: "bank_statement",
                      requestedDocumentNumber: null,
                      requestedDocumentDate: "2025-01-31",
                      requestedCounterparty: null,
                      status: "found" as const,
                      matchedDocumentId: "d5",
                      matchNotes: null,
                      candidateDocumentIds: [],
                    },
                    {
                      id: `${j.id}-i5`,
                      requestedDescription: "Платежно нареждане от 20.02.2025",
                      requestedDocumentType: null,
                      requestedDocumentNumber: null,
                      requestedDocumentDate: "2025-02-20",
                      requestedCounterparty: null,
                      status: "not_found" as const,
                      matchedDocumentId: null,
                      matchNotes: "No payment slip found for this date — bank API integration not yet available",
                      candidateDocumentIds: [],
                    },
                    {
                      id: `${j.id}-i6`,
                      requestedDescription: "Анекс или допълнително споразумение",
                      requestedDocumentType: null,
                      requestedDocumentNumber: null,
                      requestedDocumentDate: null,
                      requestedCounterparty: null,
                      status: "ambiguous" as const,
                      matchedDocumentId: null,
                      matchNotes: "Multiple annexes and agreements found — please select the correct one",
                      candidateDocumentIds: ["d6", "d17"],
                    },
                  ],
                }
              : j
          )
        );
        showSuccessToast("Tax request processing complete");
      }, 3000);
    },
    []
  );

  const handleJobUpdate = useCallback((updatedJob: TaxRequestJob) => {
    setJobs((prev) => prev.map((j) => (j.id === updatedJob.id ? updatedJob : j)));
  }, []);

  if (selectedJob) {
    return (
      <TaxRequestReviewPage
        job={selectedJob}
        onBack={() => setSelectedJobId(null)}
        onJobUpdate={handleJobUpdate}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">NRA Tax Requests</h1>
        <Button size="sm" onClick={() => setUploadDialogOpen(true)}>
          <Plus className="w-4 h-4 mr-1.5" />
          New Tax Request
        </Button>
      </div>

      <TaxRequestList jobs={jobs} onViewJob={setSelectedJobId} />

      <TaxRequestUploadDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        onSubmit={handleNewRequest}
      />
    </div>
  );
}
